import {
  createBrowserRouter,
  createRoutesFromElements,
  defer,
  Outlet,
  Route,
} from 'react-router-dom';

import {
  API_ENDPOINT_KEYS,
  FEATURE_FLAGS,
  SERVICE_AUTH_ENDPOINTS,
} from '~common';
import { FeatureFlagsRenderer } from '~components/atoms';
import { Can } from '~components/atoms/Can';
import {
  AccessDeniedPage,
  EmailSentSuccessPage,
  ErrorPage,
  ForgotPasswordPage,
  HomePage,
  LoginPage,
  ResetPasswordPage,
} from '~components/pages';
import { AuthTemplate, LoginLayout, MainLayout } from '~components/templates';
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  RESET_PASSWORD_PATH,
} from '~configs';
import {
  AuthProvider,
  ForgotPasswordProvider,
  RequireAuth,
  ResetPasswordProvider,
} from '~providers';
import {
  featureFlagsService,
  inMemoryJWTService,
  permissionRolloutConfigService,
  postAuthRequest,
} from '~services';

const flagsLoader = async () => {
  try {
    // Fetch flags from service rental
    await featureFlagsService.handleFetchFeatureFlags();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Flags cached failed with reason: ', err);
  }
};

const permissionRolloutConfigLoader = async () => {
  try {
    // Fetch per-permission rollout config from service-auth
    await permissionRolloutConfigService.load();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Permission rollout config failed with reason: ', err);
  }
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* LOGIN ROUTE */}
      <Route path={LOGIN_PATH} element={<LoginLayout />}>
        <Route
          index
          loader={async () => {
            await flagsLoader();
            const userPromise = inMemoryJWTService.getNewAccessToken();
            return defer({
              userPromise,
            });
          }}
          element={
            <AuthProvider>
              <LoginPage />
            </AuthProvider>
          }
        />
      </Route>
      {/* FORGOT PASSWORD ROUTE */}
      <Route path={FORGOT_PASSWORD_PATH} element={<LoginLayout />}>
        <Route
          loader={async () => {
            await flagsLoader();
            return null;
          }}
          element={
            <ForgotPasswordProvider>
              <FeatureFlagsRenderer
                ffKey={FEATURE_FLAGS.WEB_DASHBOARD_RESET_PASSWORD}
                children={<Outlet />}
                fallBackChildren={<ErrorPage />}
              />
            </ForgotPasswordProvider>
          }
        >
          <Route index element={<ForgotPasswordPage />} />
          <Route path="email-sent-success" element={<EmailSentSuccessPage />} />
        </Route>
      </Route>
      {/* RESET PASSWORD ROUTE */}
      <Route path={RESET_PASSWORD_PATH} element={<LoginLayout />}>
        <Route
          path=":token"
          loader={async ({ params }) => {
            await flagsLoader();

            const { token } = params;

            const validatePromise = postAuthRequest({
              path: `${
                SERVICE_AUTH_ENDPOINTS[
                  API_ENDPOINT_KEYS.FORGOT_PASSWORD_VALIDATE
                ]
              }?token=${token}`,
              ignoreAuthHeader: true,
            });

            return defer({
              validatePromise,
            });
          }}
          element={
            <ResetPasswordProvider>
              <FeatureFlagsRenderer
                ffKey={FEATURE_FLAGS.WEB_DASHBOARD_RESET_PASSWORD}
                children={<ResetPasswordPage />}
                fallBackChildren={<ErrorPage />}
              />
            </ResetPasswordProvider>
          }
        />
      </Route>
      {/* MAIN ROUTE */}
      <Route
        element={<AuthTemplate />}
        loader={async () => {
          await flagsLoader();

          // This is to prevent calling another new access token
          // because login already given a renewed one
          let token = null;
          try {
            token = await inMemoryJWTService.getValidToken();
          } catch (err) {
            // no in-memory token or failed to refresh to get new token
            // eslint-disable-next-line no-console
            console.log(
              'Failed to retrieve the current valid token with error:\n',
              err,
              '\nAttempting to renew the access token.',
            );
          }

          // If a valid token exists, resolve it immediately, otherwise fetch a new token
          let userPromise;

          if (token) {
            userPromise = Promise.resolve(token);
            permissionRolloutConfigLoader();
          } else {
            userPromise = inMemoryJWTService
              .getNewAccessToken()
              .then((token) => {
                // eslint-disable-next-line no-console
                console.log('Renew token successfully!');
                permissionRolloutConfigLoader();
                return token;
              })
              .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('Failed to renew token with error:', err);
                throw err;
              });
          }

          return defer({
            userPromise,
          });
        }}
      >
        <Route
          path="/"
          element={
            <RequireAuth>
              <MainLayout />
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <Can
                capability="pinning.workflow.access"
                fallback={<AccessDeniedPage />}
              >
                <HomePage />
              </Can>
            }
          />
        </Route>
      </Route>
      {/* OTHER ROUTE */}
      <Route path="*" element={<ErrorPage />} />;
    </>,
  ),
);
